import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock, MessageSquarePlus, X } from 'lucide-react';
import { FramingChat } from './FramingChat';
import { PhoneVerifyModal } from '../ui';
import { useChatPaneStore } from '../../store/chatPaneStore';
import { useAuthStore } from '../../store/authStore';
import { useCreateResearchJob } from '../../hooks/useCreateResearchJob';
import { isTwa } from '../../utils/platform';

const MOBILE_MQ = '(max-width: 1023.98px)';
const isMobile = () => window.matchMedia(MOBILE_MQ).matches;

/**
 * Global "Ask a legal question" research chat — available on every
 * authenticated page (research doc: one consolidated, clearly labeled entry
 * point on all pages).
 *
 * Desktop (lg+): a right-docked, NON-modal panel (~420px) rendered as a flex
 * sibling of <main> inside Layout, so it PUSHES content instead of overlaying
 * it (Windows 11 Copilot / Chrome Gemini pattern). No focus trap; Escape or
 * the header X minimizes it.
 *
 * Mobile: an accent FAB above the BottomNav opens a near-full-screen (94dvh)
 * bottom sheet with drag-handle affordance, explicit X, scrim, body scroll
 * lock, and Android-back dismissal via a pushState history sentinel.
 *
 * The pane stays MOUNTED when closed (display toggling) so a half-framed
 * question survives open/close and route changes. Open state persists in
 * localStorage (chatPaneStore).
 *
 * On /app/research/new the pane and FAB are hidden — that page IS the chat.
 * The navbar button there focuses the page's own composer instead of toggling
 * (decision documented in Navbar.tsx).
 *
 * position:fixed safety: this renders as a sibling of the animated <main>
 * (never inside it), so no ancestor carries a transform/animation — the house
 * "portal fixed overlays" rule is satisfied by placement.
 */
export function ChatPane() {
  const isOpen = useChatPaneStore((s) => s.isOpen);
  const openStore = useChatPaneStore((s) => s.open);
  const closeStore = useChatPaneStore((s) => s.close);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const onIntakePage = pathname === '/app/research/new';
  const visible = isOpen && !onIntakePage;

  // Remount key: after a successful job creation the transcript is cleared so
  // the next open starts fresh (a draft only needs to survive until it's run).
  const [chatKey, setChatKey] = useState(0);
  const [matterId, setMatterId] = useState<string | null>(null);

  // Android-back dismissal: a same-URL history sentinel is pushed when the
  // sheet opens on mobile; popstate (system back) closes the sheet.
  const pushedRef = useRef(false);

  const creator = useCreateResearchJob(() => {
    const wasPushed = pushedRef.current;
    pushedRef.current = false;
    closeStore();
    setChatKey((k) => k + 1);
    setMatterId(null);
    // true → replace-navigate, turning the sentinel entry into the job page
    // so pressing back from the memo returns to where the user was.
    return wasPushed;
  });

  const requestClose = useCallback(() => {
    if (pushedRef.current) {
      // Let the popstate handler do the closing — keeps history balanced.
      window.history.back();
    } else {
      closeStore();
    }
  }, [closeStore]);

  // Push the back-button sentinel when the sheet becomes visible on mobile.
  useEffect(() => {
    if (visible && isMobile() && !pushedRef.current) {
      window.history.pushState({ uravaChatPane: true }, '');
      pushedRef.current = true;
    }
  }, [visible]);

  useEffect(() => {
    const onPop = () => {
      if (pushedRef.current) {
        pushedRef.current = false;
        useChatPaneStore.getState().close();
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Escape closes (non-modal convenience). Skipped while the phone-OTP modal
  // is up — that modal owns Escape for itself.
  const phoneModalOpen = creator.phoneModal.open;
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !phoneModalOpen) requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, phoneModalOpen, requestClose]);

  // Body scroll lock — mobile sheet only (the desktop dock is non-modal).
  useEffect(() => {
    if (!visible) return;
    const mq = window.matchMedia(MOBILE_MQ);
    const apply = () => {
      document.body.style.overflow = mq.matches ? 'hidden' : '';
    };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      document.body.style.overflow = '';
    };
  }, [visible]);

  const limitReached = !!user && user.monthly_jobs_used >= user.monthly_jobs_limit;
  const jobsLeft = user ? Math.max(0, user.monthly_jobs_limit - user.monthly_jobs_used) : null;

  return (
    <>
      {/* Mobile scrim — sheet is modal on small screens */}
      {visible && (
        <button
          type="button"
          aria-label="Close research chat"
          onClick={requestClose}
          className="lg:hidden fixed inset-0 z-[60] bg-legal-900/50 cursor-default"
        />
      )}

      {/* The pane. Stays mounted when closed (display:none) so the draft
          transcript survives; classes flip it between the mobile fixed sheet
          and the desktop in-flow docked panel. animate-slide-up respects the
          global prefers-reduced-motion kill-switch in index.css. */}
      <aside
        role="complementary"
        aria-label="Legal research chat"
        aria-hidden={!visible}
        className={`${visible ? 'flex' : 'hidden'} flex-col bg-white
          fixed inset-x-0 bottom-0 z-[70] h-[94dvh] rounded-t-2xl shadow-elevation-3 animate-slide-up
          pb-[env(safe-area-inset-bottom)]
          lg:sticky lg:inset-auto lg:top-0 lg:self-start lg:z-auto lg:h-[calc(100vh-64px)]
          lg:rounded-none lg:shadow-none lg:pb-0
          lg:w-[420px] lg:shrink-0 lg:border-l lg:border-legal-200`}
      >
        {/* Drag-handle affordance (visual only; explicit X + back both close) */}
        <div aria-hidden="true" className="lg:hidden pt-2 flex justify-center">
          <div className="h-1 w-10 rounded-full bg-legal-200" />
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-legal-100">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-legal-900">Legal research</h2>
            {jobsLeft !== null && (
              <p className="text-[11px] text-legal-400 truncate">
                {jobsLeft} of {user!.monthly_jobs_limit} research
                {user!.monthly_jobs_limit === 1 ? '' : 'es'} left this month
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Minimize research chat"
            data-track="research-chat-close"
            className="p-2 -mr-2 text-legal-400 hover:text-legal-700 rounded-lg transition-colors press flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {limitReached ? (
          <div className="flex-1 min-h-0 overflow-y-auto p-6 text-center">
            <div className="flex justify-center mb-4 mt-4">
              <div className="p-3 bg-amber-50 rounded-full">
                <Lock className="h-6 w-6 text-amber-500" />
              </div>
            </div>
            <h3 className="text-base font-semibold text-legal-900 mb-2">Monthly limit reached</h3>
            <p className="text-legal-500 text-sm mb-5">
              You've used all {user!.monthly_jobs_limit} research
              {user!.monthly_jobs_limit === 1 ? '' : 'es'} on your plan this month.
              {isTwa() ? ' Upgrade at urava.app for more.' : ' Upgrade for more.'}
            </p>
            {!isTwa() && (
              <button
                type="button"
                onClick={() => {
                  requestClose();
                  navigate('/pricing');
                }}
                data-track="research-chat-wall-see-plans"
                className="px-6 py-2.5 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors"
              >
                See plans
              </button>
            )}
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col px-3 sm:px-4 pt-1 pb-3">
            {creator.error && (
              <p className="mb-2 mt-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm break-words">
                {creator.error}
              </p>
            )}
            <div className="flex-1 min-h-0">
              <FramingChat
                key={chatKey}
                fill
                isSubmitting={creator.isSubmitting}
                matterId={matterId}
                onMatterChange={setMatterId}
                onAccept={({ question, jurisdiction, law_domain, input_document_id, output_language }) =>
                  creator.submit({
                    description: question,
                    input_type: input_document_id ? 'document' : 'text',
                    input_document_id,
                    matter_id: matterId ?? undefined,
                    jurisdiction: jurisdiction || 'All India',
                    law_domain,
                    output_language,
                  })
                }
              />
            </div>
          </div>
        )}
      </aside>

      {/* Mobile FAB — above the BottomNav; hidden while the sheet is open and
          on /app/research/new (that page IS the chat). */}
      {!isOpen && !onIntakePage && (
        <button
          type="button"
          onClick={openStore}
          aria-label="Ask a legal question"
          data-track="research-chat-fab"
          data-tour="research-chat-fab"
          className="lg:hidden fixed right-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-40 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-legal-900 shadow-elevation-3 press"
        >
          <MessageSquarePlus className="h-6 w-6" />
        </button>
      )}

      {/* Phone gate works from the pane exactly as from the intake page. */}
      <PhoneVerifyModal
        open={creator.phoneModal.open}
        onClose={creator.phoneModal.onClose}
        onVerified={creator.phoneModal.onVerified}
      />
    </>
  );
}
